/**
 * Main factory function for creating scene capabilities from templates
 */

import { deepMerge, addCapabilities } from './utils/deepMerge.js';
import { createSingleRelayTemplate } from './templates/families/relay/singleRelay.js';
import { createDualRelayTemplate } from './templates/families/relay/dualRelay.js';
import {
	createTriRelayTemplate,
	createTriRelayPMTemplate,
} from './templates/families/relay/tri_relay.js';
import {
	createQuadRelayTemplate,
	createQuadRelayPMTemplate,
} from './templates/families/relay/quad_relay.js';
import { createSingleDimmerTemplate } from './templates/families/dimmer/singleDimmer.js';
import { createDualDimmerTemplate } from './templates/families/dimmer/dualDimmer.js';
import { createRgbwTemplate } from './templates/families/light/rgbw.js';
import { createTemperatureHumiditySensorTemplate } from './templates/families/sensor/temperatureHumidity.js';
import { createMultiInputSensorTemplate } from './templates/families/sensor/multiInput.js';
import { createEmeterTemplate } from './templates/families/emeter/emeter.js';
import { createRollerTemplate } from './templates/families/roller/roller.js';
import { createDualRollerTemplate } from './templates/families/roller/dual_roller.js';

// Gen1 templates
import { createGen1SingleRelayTemplate } from './gen1/singleRelay.js';
import { createGen1SingleRelayPMTemplate } from './gen1/singleRelayPM.js';
import { createGen1DualRelayTemplate, createGen1DualRelayPMTemplate } from './gen1/dualRelay.js';
import { createGen1QuadRelayPMTemplate } from './gen1/quadRelay.js';
import { createGen1LightTemplate } from './gen1/light.js';
import { createGen1SingleDimmerTemplate, createGen1DualDimmerTemplate } from './gen1/dimmer.js';

/**
 * Create scene capabilities from template with optional overrides
 *
 * @param {object} config - Configuration object
 * @param {string} config.template - Template name ('single_relay', 'dual_relay', etc.)
 * @param {object} [config.overrides] - Device-specific capability overrides (optional)
 * @param {object} [config.add] - Additional capabilities to add without replacing base (optional)
 * @returns {object} Complete sceneCapabilities object
 */
export function createSceneCapabilities(config) {
	const { template, overrides = {}, add = {} } = config;

	// Get base template
	let baseCapabilities = getTemplate(template);

	if (!baseCapabilities) {
		console.warn(`Template "${template}" not found. Using empty capabilities.`);

		return {
			deviceType: template,
			channels: [],
			actions: {},
			conditions: {},
			triggers: {},
		};
	}

	// Clone to avoid mutating template
	let capabilities = JSON.parse(JSON.stringify(baseCapabilities));

	// Add capabilities without replacing
	if (Object.keys(add).length > 0) {
		capabilities = addCapabilities(capabilities, add);
	}

	// Apply overrides
	if (Object.keys(overrides).length > 0) {
		capabilities = deepMerge(capabilities, overrides);
	}

	return capabilities;
}

/**
 * Get template by name
 * @param {string} templateName - Template identifier
 * @returns {object|null} Template capabilities
 */
function getTemplate(templateName) {
	const templates = {
		// Gen2+ templates
		single_relay: createSingleRelayTemplate,
		dual_relay: createDualRelayTemplate,
		tri_relay: createTriRelayTemplate,
		tri_relay_pm: createTriRelayPMTemplate,
		quad_relay: createQuadRelayTemplate,
		quad_relay_pm: createQuadRelayPMTemplate,
		single_dimmer: createSingleDimmerTemplate,
		dual_dimmer: createDualDimmerTemplate,
		rgbw: createRgbwTemplate,
		temperature_humidity_sensor: createTemperatureHumiditySensorTemplate,
		multi_input_sensor: createMultiInputSensorTemplate,
		emeter: createEmeterTemplate,
		roller: createRollerTemplate,
		dual_roller: createDualRollerTemplate,

		// Gen1 templates
		single_relay_gen1: createGen1SingleRelayTemplate,
		single_relay_pm_gen1: createGen1SingleRelayPMTemplate,
		dual_relay_gen1: createGen1DualRelayTemplate,
		dual_relay_pm_gen1: createGen1DualRelayPMTemplate,
		quad_relay_pm_gen1: createGen1QuadRelayPMTemplate,
		light_gen1: createGen1LightTemplate,
		single_dimmer_gen1: createGen1SingleDimmerTemplate,
		dual_dimmer_gen1: createGen1DualDimmerTemplate,
	};

	const templateFn = templates[templateName];
	return templateFn ? templateFn() : null;
}

/**
 * Export individual template creators for direct use
 */
export {
	// Gen2+ templates
	createSingleRelayTemplate,
	createDualRelayTemplate,
	createTriRelayTemplate,
	createTriRelayPMTemplate,
	createQuadRelayTemplate,
	createQuadRelayPMTemplate,
	createSingleDimmerTemplate,
	createDualDimmerTemplate,
	createRgbwTemplate,
	createTemperatureHumiditySensorTemplate,
	createMultiInputSensorTemplate,
	createEmeterTemplate,
	createRollerTemplate,
	createDualRollerTemplate,
	// Gen1 templates
	createGen1SingleRelayTemplate,
	createGen1DualRelayPMTemplate,
	createGen1QuadRelayPMTemplate,
	createGen1SingleRelayPMTemplate,
	createGen1DualRelayTemplate,
	createGen1LightTemplate,
	createGen1SingleDimmerTemplate,
	createGen1DualDimmerTemplate,
};
